import { router } from '@inertiajs/react';
import { Image as ImageIcon, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import type { SharedProps } from '@/types';

interface CoverUploadProps {
  eventSlug: string;
  gameId: string;
  currentCover: string | null;
  gameTitle: string;
}

export function CoverUpload({
  eventSlug,
  gameId,
  currentCover,
  gameTitle,
}: CoverUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isPending, setIsPending] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentCover);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64String = e.target?.result as string;
      setPreviewUrl(base64String);

      // Upload to server
      setIsPending(true);
      router.patch(
        `/admin/${eventSlug}/arcade/${gameId}/cover`,
        { image: base64String },
        {
          preserveScroll: true,
          onSuccess: (page) => {
            const { flash } = page.props as unknown as SharedProps;

            if (flash.alert) {
              toast.error(flash.alert);
              // Reset preview on error
              setPreviewUrl(currentCover);
            } else {
              toast.success(flash.notice ?? 'Cover image updated successfully');
            }
          },
          onError: () => {
            toast.error('Failed to update cover');
            setPreviewUrl(currentCover);
          },
          onFinish: () => setIsPending(false),
        },
      );
    };

    reader.onerror = () => {
      toast.error('Failed to read image file');
    };

    reader.readAsDataURL(file);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4">
        {previewUrl ? (
          <div className="relative aspect-video w-full overflow-hidden rounded-lg border-2 border-border bg-muted">
            <img
              src={previewUrl}
              alt={`Cover for ${gameTitle}`}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        ) : (
          <div className="flex aspect-video w-full items-center justify-center rounded-lg border-2 border-border border-dashed bg-muted">
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <ImageIcon className="h-12 w-12" />
              <p className="text-sm">No cover image</p>
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          disabled={isPending}
        />

        <Button
          onClick={handleButtonClick}
          disabled={isPending}
          variant="outline"
          className="w-full"
        >
          <Upload className="mr-2 h-4 w-4" />
          {isPending ? 'Uploading...' : 'Upload Cover Image'}
        </Button>

        <p className="text-muted-foreground text-xs">
          Recommended: 16:9 aspect ratio, max 2MB (PNG, JPG, GIF)
        </p>
      </div>
    </div>
  );
}
